import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils.ts";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs.ts";
import { CampaignSchema } from "src/api/client-api/useCampaign.ts";

export const getAllQuestionnairesKey = "allQuestionnaires" as const;

type Options = {
    cacheTime?: number;
    params?: AxiosParams;
};

export const useGetAllQuestionnaires = (options?: Options) => {
    const { cacheTime = 0, params = {} } = options || {};

    const query = useQuery({
        queryKey: [getAllQuestionnairesKey, params],
        staleTime: 0,
        cacheTime: cacheTime,
        queryFn: async () => {
            const url = "/api/questionnaires";

            const request = await loggedInApiCall.get(url, {
                params,
            });

            const totalItems = request.data["hydra:totalItems"];
            const members = request.data["hydra:member"];
            const data = zodParseWithDetailedLog(members, QuestionnaireSchema.array(), {
                operation: "GET",
                endpoint: "/questionnaires",
            });
            return {
                questionnaires: data,
                totalItems: totalItems,
            };
        },
        onError: () => {
            return {
                questionnaires: [],
                totalItems: 0,
            };
        },
    });

    const isReady = isQueryReady(query, true);
    return {
        ...query.data,
        isReady,
        ...query,
    };
};

const ReferenceMetadataSchema = z.object({
    id: z.number(),
    name: z.string(),
});

const SsqVersionAssignmentSchema = z.object({
    id: z.number(),
    ssqId: z.number(),
    ssqVersionId: z.number(),
    campaign: CampaignSchema,
});

export type SsqVersionAssignment = z.infer<typeof SsqVersionAssignmentSchema>;

const QuestionnaireVersionSchema = z.object({
    id: z.number(),
    versionMajor: z.number(),
    versionMinor: z.number(),
    status: z.number(),
    referenceMetadata: ReferenceMetadataSchema,
    releaseNotes: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    assignments: z.array(SsqVersionAssignmentSchema),
});

export type QuestionnaireVersion = z.infer<typeof QuestionnaireVersionSchema>;

export const QuestionnaireSchema = z.object({
    id: z.number(),
    title: z.string(),
    currentVersion: QuestionnaireVersionSchema,
    versions: z.array(QuestionnaireVersionSchema),
    objects: z.array(z.string()),
    targetEntityTypes: z.array(z.string()),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    createdBy: z.object({ firstname: z.string(), lastname: z.string() }).optional(),
    updatedBy: z.object({ firstname: z.string(), lastname: z.string() }).optional(),
});

export type Questionnaire = z.infer<typeof QuestionnaireSchema>;
